import React, { useState, useEffect, useRef } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import axios from 'axios';
import { extractChatMessageText, parseJsonReply } from '../utils/openai';
import "./ChatApp.css";

const LocalApiUrl = "/api";
const GPT_MODEL   = "gpt-5.4";

async function openaiChat(body) {
  const res = await axios.post(`${LocalApiUrl}/openai`, body);
  return res.data;
}

const ChatApp = ({ isOpen, onClose, selectedBook }) => {
  const [characters,         setCharacters]         = useState([]);
  const [selectedCharacter,  setSelectedCharacter]  = useState(null);
  const [messages,           setMessages]           = useState([]);
  const [inputText,          setInputText]          = useState("");
  const [isThinking,         setIsThinking]         = useState(false);
  const [copied,             setCopied]             = useState(null);
  const bottomRef = useRef(null);

  // fetch characters when book changes
  useEffect(() => {
    if (!selectedBook) return;
    setCharacters([]);
    setSelectedCharacter(null);
    setMessages([]);

    const fetchCharacters = async () => {
      try {
        const data = await openaiChat({
          model: GPT_MODEL,
          messages: [{
            role: "user",
            content: `List the main characters from "${selectedBook.title}" by ${selectedBook.author}. Return JSON as {"characters":["Name1","Name2"]}. Max 8 characters.`,
          }],
          max_completion_tokens: 300,
          temperature: 0.3,
          response_format: { type: "json_object" },
        });
        const parsed = parseJsonReply(extractChatMessageText(data));
        setCharacters(Array.isArray(parsed?.characters) ? parsed.characters : []);
      } catch (err) {
        console.error("fetchCharacters error:", err);
      }
    };

    fetchCharacters();
  }, [selectedBook]);

  // scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isThinking]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = inputText.trim();
    if (!text || !selectedCharacter || isThinking) return;

    const userMsg = { text, sender: "user" };
    setMessages(prev => [...prev, userMsg]);
    setInputText("");
    setIsThinking(true);

    try {
      const apiMessages = [
        {
          role: "system",
          content: `You are ${selectedCharacter} from "${selectedBook.title}" by ${selectedBook.author}. Stay completely in character — speak, think, and react as ${selectedCharacter} would. Never break character.`,
        },
        ...messages.map(m => ({
          role: m.sender === "character" ? "assistant" : "user",
          content: m.text,
        })),
        { role: "user", content: text },
      ];

      const data = await openaiChat({
        model: GPT_MODEL, messages: apiMessages, max_completion_tokens: 1200, temperature: 0.85,
      });

      setMessages(prev => [...prev, {
        text: extractChatMessageText(data) || "…(the character fell silent)",
        sender: "character",
      }]);
    } catch (err) {
      console.error("chat error:", err);
      setMessages(prev => [...prev, {
        text: "…(the character fell silent)",
        sender: "character",
      }]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleCopy = (idx) => {
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!isOpen || !selectedBook) return null;

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      {/* Modal */}
      <div className="w-full max-w-lg bg-ink-surface border border-ink-border rounded-2xl
                      shadow-card flex flex-col overflow-hidden" style={{ maxHeight: "90vh" }}>

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-ink-border">
          <div>
            <p className="text-gold text-[10px] uppercase tracking-widest mb-0.5">Character Chat</p>
            <h3 className="font-serif text-cream text-lg leading-tight">{selectedBook.title}</h3>
            <p className="text-cream-muted text-xs">{selectedBook.author}</p>
          </div>
          <button
            onClick={onClose}
            className="text-cream-muted hover:text-cream transition-colors text-xl leading-none mt-1"
          >
            ✕
          </button>
        </div>

        {/* Character selector */}
        <div className="p-4 border-b border-ink-border">
          {characters.length === 0 ? (
            <div className="flex items-center gap-2 text-cream-muted text-sm">
              <span className="w-3 h-3 border border-t-gold border-ink-border rounded-full animate-spin" />
              Loading characters…
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {characters.map(char => (
                <button
                  key={char}
                  onClick={() => { setSelectedCharacter(char); setMessages([]); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    selectedCharacter === char
                      ? "bg-gold text-ink-base border-gold"
                      : "border-ink-border text-cream-dim hover:border-gold hover:text-gold"
                  }`}
                >
                  {char}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 min-h-0">
          {!selectedCharacter && (
            <p className="text-cream-muted text-sm text-center mt-8">
              Select a character above to begin
            </p>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex flex-col gap-1 max-w-[80%] ${msg.sender === "user" ? "self-end items-end" : "self-start items-start"}`}
            >
              <div
                className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.sender === "user"
                    ? "bg-gold text-ink-base rounded-br-sm"
                    : "bg-ink-raised text-cream border border-ink-border rounded-bl-sm"
                }`}
              >
                {msg.text}
              </div>
              {msg.sender === "character" && (
                <CopyToClipboard
                  text={`${selectedCharacter}: ${msg.text}\n\n— from StoryChats (storychats.com)`}
                  onCopy={() => handleCopy(i)}
                >
                  <button className="text-[10px] text-cream-muted hover:text-gold transition-colors">
                    {copied === i ? "Copied!" : "Copy"}
                  </button>
                </CopyToClipboard>
              )}
            </div>
          ))}

          {isThinking && (
            <div className="self-start max-w-[80%]">
              <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-ink-raised border border-ink-border">
                <div className="flex gap-1.5 items-center">
                  <span className="w-1.5 h-1.5 bg-cream-muted rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-cream-muted rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-cream-muted rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="p-4 border-t border-ink-border flex gap-2">
          <input
            type="text"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            placeholder={selectedCharacter ? `Ask ${selectedCharacter}…` : "Select a character first"}
            disabled={!selectedCharacter || isThinking}
            className="sc-input flex-1 text-sm disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!selectedCharacter || isThinking || !inputText.trim()}
            className="sc-btn-gold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatApp;
